/**
 * AdminSegmentImagesCard — admin manages the wide banner image displayed
 * at the top of each Instant Pricing segment (Catering, Photography, etc.).
 *
 * Each row supports: (a) upload a new image via /api/upload (returns a
 * relative URL), (b) paste any http(s) URL, (c) reset to the curated default.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, FloppyDisk, Spinner, ArrowsClockwise, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_URL}${u}`;
};

const AdminSegmentImagesCard = () => {
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/segment-images`,
        { withCredentials: true }
      );
      setDefaults(data.defaults || {});
      setOverrides(data.overrides || {});
      setLabels(data.labels || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load segment images');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const segmentKeys = Object.keys(defaults);

  const valueFor = (k) => overrides[k] || defaults[k] || '';
  const isOverridden = (k) => !!overrides[k];

  const setOverride = (k, url) => {
    setOverrides((cur) => {
      const next = { ...cur };
      if (url) next[k] = url; else delete next[k];
      return next;
    });
  };

  const handleUpload = async (k, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files (JPG, PNG, WEBP, GIF) accepted');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large — max 5 MB');
      return;
    }
    setUploadingKey(k);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setOverride(k, data.url);
      toast.success(`${labels[k] || k} — image uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploadingKey(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only persist actual overrides (skip blanks)
      const images = {};
      segmentKeys.forEach((k) => { images[k] = overrides[k] || ''; });
      await axios.put(`${BACKEND_URL}/api/admin/segment-images`,
        { images }, { withCredentials: true });
      toast.success('Segment banners saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-segment-images-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImageIcon size={20} className="text-brand-primary" weight="fill" /> Segment Banner Images
        </CardTitle>
        <CardDescription>
          Wide banner image shown at the top of each Instant Pricing segment.
          Customers see the same banner across all event types. Upload your own or
          paste any public URL — leave blank to fall back to the curated default.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            {segmentKeys.map((k) => {
              const url = valueFor(k);
              const overridden = isOverridden(k);
              return (
                <div key={k}
                  className="flex flex-col sm:flex-row gap-3 p-3 border border-[#E8E1D5] bg-sand-50"
                  data-testid={`admin-segment-row-${k}`}>
                  <div className="w-full sm:w-40 h-20 bg-stone-200 overflow-hidden flex-shrink-0">
                    {url ? (
                      <img src={resolveImg(url)} alt={labels[k] || k}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-xs text-text-secondary">No image</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm">
                        {labels[k] || k}
                        {overridden && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-brand-primary text-sand-50">CUSTOM</span>}
                      </div>
                      {overridden && (
                        <button onClick={() => setOverride(k, '')}
                          className="text-xs text-text-secondary hover:text-brand-primary flex items-center gap-1"
                          data-testid={`admin-segment-reset-${k}`}>
                          <ArrowsClockwise size={11} /> Reset
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input value={overrides[k] || ''}
                        onChange={(e) => setOverride(k, e.target.value)}
                        placeholder={defaults[k] || 'https://… or /api/uploads/…'}
                        className="h-8 rounded-none border-[#E8E1D5] flex-1 text-xs font-mono"
                        data-testid={`admin-segment-url-${k}`} />
                      <input
                        ref={(el) => { fileInputs.current[k] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => handleUpload(k, e.target.files?.[0])}
                        data-testid={`admin-segment-file-${k}`}
                      />
                      <Button variant="outline"
                        onClick={() => fileInputs.current[k]?.click()}
                        disabled={uploadingKey === k}
                        className="rounded-none border-[#E8E1D5] h-8 px-3"
                        data-testid={`admin-segment-upload-${k}`}>
                        {uploadingKey === k
                          ? <Spinner size={12} className="mr-1 animate-spin" />
                          : <UploadSimple size={12} className="mr-1" />}
                        Upload
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-end pt-3 border-t border-[#E8E1D5]">
              <Button onClick={save} disabled={saving}
                className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-60"
                data-testid="admin-segment-images-save">
                {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                Save banners
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSegmentImagesCard;
